
import { useEffect, useState } from 'react'

  const API_BASE_URL = import.meta.env.PROD
    ? 'https://api.dev.hightrust.com.au'
    : 'http://localhost:3001';

// Unified endpoints for both dev and production
const endpoints = {
  auth: {
    login: '/api/login',
    logout: '/api/logout'
  },
  user: '/api/me',
  protected: '/api/protected',
  health: '/api/health'
}

interface User {
  name?: string
  email?: string
  picture?: string
  [key: string]: any
}

export default function App() {
  const [message, setMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [profileData, setProfileData] = useState('')
  const [user, setUser] = useState<User | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [checkingAuth, setCheckingAuth] = useState(true)

  // Check authentication status on load
  useEffect(() => {
    checkAuthStatus()
  }, [])

  async function checkAuthStatus() {
    setCheckingAuth(true)
    try {
      const url = API_BASE_URL ? `${API_BASE_URL}${endpoints.user}` : endpoints.user
      const res = await fetch(url, { credentials: 'include' })

      if (res.ok) {
        const data = await res.json()
        // If we get a successful response with user data, user is authenticated
        if (data && (data.sub || data.email || data.name)) {
          setUser(data)
          setIsAuthenticated(true)
        } else {
          setUser(null)
          setIsAuthenticated(false)
        }
      } else {
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (e) {
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setCheckingAuth(false)
    }
  }

  const handleLogin = () => {
    const loginUrl = API_BASE_URL ? `${API_BASE_URL}${endpoints.auth.login}` : endpoints.auth.login
    window.location.href = loginUrl
  }

  const handleLogout = async () => {
    const logoutUrl = API_BASE_URL ? `${API_BASE_URL}${endpoints.auth.logout}` : endpoints.auth.logout
    window.location.href = logoutUrl
  }

  async function pingApi() {
    setLoading(true)
    try {
      const url = API_BASE_URL ? `${API_BASE_URL}${endpoints.health}` : endpoints.health
      console.log('Making request to:', url)
      console.log('API_BASE_URL:', API_BASE_URL)
      console.log('endpoints.health:', endpoints.health)
      const res = await fetch(url, { credentials: 'include' })
      console.log('Response status:', res.status)
      console.log('Response headers:', Object.fromEntries(res.headers.entries()))
      const data = await res.json()
      setMessage(JSON.stringify(data, null, 2))
    } catch (e) {
      console.error('Fetch error:', e)
      setMessage(String(e))
    } finally {
      setLoading(false)
    }
  }

  async function getProfile() {
    setLoading(true)
    try {
      const url = API_BASE_URL ? `${API_BASE_URL}${endpoints.user}` : endpoints.user
      const res = await fetch(url, { credentials: 'include' })
      const data = await res.json()
      setProfileData(JSON.stringify(data, null, 2))
    } catch (e) {
      setProfileData(String(e))
    } finally {
      setLoading(false)
    }
  }

  async function callProtectedApi() {
    setLoading(true)
    try {
      const url = API_BASE_URL ? `${API_BASE_URL}${endpoints.protected}` : endpoints.protected
      const res = await fetch(url, { credentials: 'include' })
      const data = await res.json()
      setMessage(JSON.stringify(data, null, 2))
    } catch (e) {
      setMessage(String(e))
    } finally {
      setLoading(false)
    }
  }

  if (checkingAuth) {
    return <div style={{ fontFamily: 'system-ui, sans-serif', padding: 24 }}>Checking authentication...</div>
  }

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', padding: 24, lineHeight: 1.5 }}>
      <h1>SPA with BFF Authentication</h1>

      {!isAuthenticated ? (
        <div>
          <p>You are not authenticated.</p>
          <button onClick={handleLogin} style={{ padding: '10px 20px', fontSize: '16px' }}>
            Log In with Auth0
          </button>
        </div>
      ) : (
        <div>
          <p>Welcome, {user?.name || user?.email}!</p>
          <button
            onClick={handleLogout}
            style={{ marginRight: 10, padding: '8px 16px' }}
          >
            Log Out
          </button>
          <button
            onClick={checkAuthStatus}
            style={{ padding: '8px 16px' }}
          >
            Refresh Auth Status
          </button>
          <hr />
          <h3>User Info (from BFF)</h3>
          <pre style={{ background: '#f7f7f7', padding: 12, borderRadius: 8, fontSize: '12px' }}>
            {JSON.stringify(user, null, 2)}
          </pre>
        </div>
      )}

      <hr />
      <h3>API Testing</h3>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
        <button onClick={pingApi} disabled={loading}>
          {loading ? 'Calling…' : 'Test /api/health'}
        </button>
        <button onClick={getProfile} disabled={loading}>
          {loading ? 'Calling…' : 'Get BFF Profile'}
        </button>
        <button onClick={callProtectedApi} disabled={loading}>
          {loading ? 'Calling…' : 'Call Protected API'}
        </button>
      </div>

      {message && (
        <div>
          <h4>API Response:</h4>
          <pre style={{background:'#f7f7f7', padding:12, borderRadius:8, fontSize: '12px'}}>
            {message}
          </pre>
        </div>
      )}

      {profileData && (
        <div>
          <h4>BFF Profile Data:</h4>
          <pre style={{background:'#e7f3ff', padding:12, borderRadius:8, fontSize: '12px'}}>
            {profileData}
          </pre>
        </div>
      )}
    </div>
  )
}